import {
  getLeoUuid,
  LeoUUID,
  getList,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { EmailCycleName, EmailCycleNameDTO } from "./emailCycleName";
/* eslint-disable import/export */
export abstract class GetEmailCycleNamesRPC implements
  LeoRPC<GetEmailCycleNamesRPC.Request, GetEmailCycleNamesRPC.Response, GetEmailCycleNamesRPC.Errors.Errors>
{
  abstract execute(
    request: GetEmailCycleNamesRPC.Request
  ): Promise<LeoRPCResult<GetEmailCycleNamesRPC.Response, GetEmailCycleNamesRPC.Errors.Errors>>;
}

export namespace GetEmailCycleNamesRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetEmailCycleNamesRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
  
  
    constructor(
    projectId: LeoUUID
    ) {
      this.projectId = projectId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      return new Request(
        projectId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `GetEmailCycleNamesRPC(projectId='${this.projectId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    cycles: EmailCycleNameDTO[];
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetEmailCycleNamesResponse: boolean = true;
  
    readonly cycles: EmailCycleName[];
  
  
    constructor(
    cycles: EmailCycleName[]
    ) {
      this.cycles = cycles;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const cycles = getList<EmailCycleNameDTO, EmailCycleName>(
        dto,
        "cycles",
        EmailCycleName.fromDTO,
        InvalidResponseCyclesError
      );
      return new Response(
        cycles
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          cycles: this.cycles.map((e: EmailCycleName) => { return e.toDTO() })
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetEmailCycleNames(cycles='${this.cycles}')`;
  };
  

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `GetEmailCycleNamesRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `GetEmailCycleNamesRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    

    export type Errors = InvalidProjectId | ProjectAlreadyArchived;
    export type ErrorsDTO = InvalidProjectIdDTO | ProjectAlreadyArchivedDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends GetEmailCycleNamesRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidResponseCyclesError extends GetEmailCycleNamesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseCyclesError";
  }
}

export class InvalidResponseInvalidProjectIdError extends GetEmailCycleNamesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends GetEmailCycleNamesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

